import { isBrowser } from "@chatfood/core-utils";
import {
  addGlobalContexts,
  newSession,
  newTracker,
  setUserId,
  trackPageView,
  trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";
import type { Analytics } from "../analytics";
import { Provider } from "../provider";
import type { ProviderListeners } from "../provider-listeners";

type IConfig = {
  collector: string;
  appId: string;
  schemaVendor: string;
};

export class Snowplow extends Provider {
  public key = "snowplow";
  protected analytics: Analytics | undefined;
  private config: IConfig;

  protected listeners: ProviderListeners = {
    setSuperProperties: (payload) => {
      // Use a generic data schema with typed context
      this.addGlobalContext(
        "business",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "business",
          account_id: payload.businessId,
          data: JSON.stringify({
            businessId: payload.businessId,
            businessName: payload.businessName,
            outletId: payload.outletId,
            outletName: payload.outletName,
            orderingMode: payload.orderingMode,
            salesChannel: payload.salesChannel,
          }),
        },
      );

      return payload;
    },

    userAnonymize: (payload) => {
      setUserId(null);

      // Use a generic data schema with typed context
      this.addGlobalContext(
        "user",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "user",
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            id: "",
            email: "",
          }),
        },
      );

      return payload;
    },

    userIdentify: (payload) => {
      setUserId(payload.id);

      // Use a generic data schema with typed context
      this.addGlobalContext(
        "user",
        `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        {
          context_type: "user",
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            id: payload.id,
            email: payload.email ?? "",
          }),
        },
      );

      return payload;
    },

    pageView: (payload) => {
      if (!isBrowser()) return payload;

      trackPageView();
      return payload;
    },

    startJourney: (payload) => {
      if (!isBrowser()) return payload;

      this.trackCustomEvent("journey", "Started Journey", {});
      return payload;
    },

    viewMenu: (payload) => {
      this.trackCustomEvent("menu", "Viewed Menu", {
        menuId: payload.id,
        menuSlug: payload.slug,
      });
      return payload;
    },

    viewItem: (payload) => {
      this.trackCustomEvent("item", "Viewed Item", {
        itemId: payload.id,
        itemName: payload.name,
        itemPrice: payload.price.value,
        currency: payload.price.currency,
        flow: payload.flow,
      });
      return payload;
    },

    addItemToBasket: (payload) => {
      this.trackCustomEvent("basket", "Added Item to Basket", {
        itemId: payload.id,
        itemName: payload.name,
        itemPrice: payload.price.value,
        currency: payload.price.currency,
        quantity: payload.quantity,
        flow: payload.flow,
      });
      return payload;
    },

    removeItemFromBasket: (payload) => {
      this.trackCustomEvent("basket", "Removed Item from Basket", {
        itemId: payload.id,
        itemName: payload.name,
        itemPrice: payload.price.value,
        currency: payload.price.currency,
        quantity: payload.quantity,
      });
      return payload;
    },

    initiateCheckout: (payload) => {
      this.trackCustomEvent("checkout", "Initiated Checkout", {
        discountCode: payload.discountCode ?? "",
        total: payload.total.value,
        currency: payload.total.currency,
        itemCount: payload.items.length,
      });
      return payload;
    },

    addPaymentInfo: (payload) => {
      this.trackCustomEvent("checkout", "Added Payment Info", {
        method: payload.method,
      });
      return payload;
    },

    selectTipAmount: (payload) => {
      this.trackCustomEvent("checkout", "Selected Tip Amount", {
        amount: payload.amount.value,
        currency: payload.amount.currency,
      });
      return payload;
    },

    placeOrder: (payload) => {
      this.trackCustomEvent("order", "Placed Order Details", {
        orderId: payload.orderId,
        orderUid: payload.orderUid,
        total: payload.total.value,
        tax: payload.tax.value,
        deliveryFee: payload.deliveryFee.value,
        tips: payload.tips.value,
        discount: payload.discount.value,
        loyalty: payload.loyalty.value,
        serviceCharges: payload.serviceCharges.value + payload.bagFee.value,
        currency: payload.total.currency,
        discountCode: payload.discountCode ?? "",
        orderingMode: payload.orderingMode,
        salesChannel: payload.salesChannel,
        itemCount: payload.items.length,
        packagingType: payload.packagingType ?? "",
      });
      return payload;
    },

    orderSuccess: (payload) => {
      this.trackCustomEvent("order", "Placed Order", {
        orderUid: payload.orderUid,
      });
      return payload;
    },

    orderFailure: (payload) => {
      this.trackCustomEvent("order", "Place Order Failed", {
        orderUid: payload.orderUid,
        reason: payload.reason ?? "Unknown",
      });
      return payload;
    },

    viewCrossSell: (payload) => {
      this.trackCustomEvent("crosssell", "Viewed Cross-Sell", {
        flow: "cross-sell",
        surface: payload.surface,
        itemCount: payload.itemCount,
      });
      return payload;
    },
  };

  constructor(config: IConfig) {
    super();
    this.config = config;
  }

  protected onInstall() {
    // Only run in browser (not during SSR or tests)
    if (!isBrowser()) return;

    if (!this.config.collector) {
      throw new Error("No Snowplow token provided");
    }

    newSession();

    newTracker("sp", this.config.collector, {
      appId: this.config.appId,
      platform: "web",
      cookieDomain: "auto",
      discoverRootDomain: true,
      cookieSameSite: "None",
      sessionCookieTimeout: 7 * 24 * 60 * 60, // 7 days in seconds
      contexts: {
        webPage: true,
        session: true, // Enable session context entity
      },
    });
  }

  protected trackCustomEvent(
    context: string,
    action: string,
    payload: Record<string, unknown>,
  ) {
    trackSelfDescribingEvent({
      event: {
        schema: `iglu:${this.config.schemaVendor}/generic_data/jsonschema/1-0-1`,
        data: {
          context_type: context,
          account_id: this.analytics?.store.superProperties.businessId ?? "",
          data: JSON.stringify({
            action,
            ...payload,
          }),
        },
      },
    });
  }

  protected addGlobalContext(
    contextKey: string,
    schema: string,
    payload: Record<string, unknown>,
  ) {
    const contextEntity = {
      schema,
      data: payload,
    };
    addGlobalContexts({ [contextKey]: contextEntity });
  }
}
